import { useReactiveVar } from '@apollo/client';
import { useNavigate } from '@hulu/react-router-dom';

import useMarkAllNotificationsAsRead from '../../../apis/graphql/notifications/markAllAsRead';
import useGetLastFourNotifications from '../../../apis/graphql/notifications/useGetLastFourNotifications';
import admanToast from '../../../components/Alert/util/admanToast';
import { StyledLoader } from '../../../components/styled';
import { userAmsUserIdVar } from '../../../constants';
import { generateSanitizedHTML } from '../Table/utils';
import {
  StyledContent,
  StyledContentTitle,
  StyledContentWrapper,
  StyledEmptyRow,
  StyledFooter,
  StyledHeader,
  StyledImage,
  StyledIndicator,
  StyledIndicatorCell,
  StyledReadAllLink,
  StyledRow,
  StyledTotalCount,
  StyledTotalWrapper,
  StyledWrapper,
} from './styled';

type Props = {
  onClose: () => void;
  total: number;
};

const HeaderFlyout = ({ onClose, total }: Props): JSX.Element => {
  const userId = useReactiveVar(userAmsUserIdVar) || '';
  const navigate = useNavigate();

  const { notifications, loading, refetch } = useGetLastFourNotifications(userId);

  const { markAllAsRead, loading: markAllAsReadLoading } = useMarkAllNotificationsAsRead(userId);

  const handleNavigateToNotifications = (): void => {
    navigate('./notifications');
    onClose();
  };

  const handleMarkAllAsRead = async (): Promise<void> => {
    try {
      await markAllAsRead(userId);
      refetch();
    } catch (error) {
      admanToast.error(`There was an error marking all as read`);
    }
  };

  if (loading || markAllAsReadLoading) {
    return (
      <StyledWrapper>
        <StyledLoader data-testid="notificationsLoader" />
      </StyledWrapper>
    );
  }

  if (!notifications.length) {
    return (
      <StyledWrapper data-testid="notificationsFlyoutHeaderEmpty">
        <StyledHeader>
          <StyledTotalWrapper>Notifications</StyledTotalWrapper>
        </StyledHeader>
        <StyledEmptyRow>You currently have no in-platform notifications.</StyledEmptyRow>
      </StyledWrapper>
    );
  }

  return (
    <StyledWrapper data-testid="notificationsFlyoutHeaderWrapper">
      <StyledHeader>
        <StyledTotalWrapper>
          Notifications
          <StyledTotalCount data-testid="totalUnreadNotifications"> ({total})</StyledTotalCount>
        </StyledTotalWrapper>
        <StyledReadAllLink data-testid="markAllAsRead" onClick={handleMarkAllAsRead}>
          Mark all as read
        </StyledReadAllLink>
      </StyledHeader>
      {notifications.map((notification) => (
        <StyledRow key={notification.id}>
          <StyledIndicatorCell>
            {!notification.acked && <StyledIndicator data-testid="notificationReadIndicator" />}
          </StyledIndicatorCell>
          <StyledImage data-testid="notificationIcon" src={notification.icon || ''} />
          <StyledContentWrapper>
            <StyledContentTitle data-testid="notificationTitle">{notification.header}</StyledContentTitle>
            <StyledContent
              data-testid="notificationText"
              $isUnread={!notification.acked}
              dangerouslySetInnerHTML={{
                __html: generateSanitizedHTML(notification.text),
              }}
            />
          </StyledContentWrapper>
        </StyledRow>
      ))}

      <StyledFooter data-testid="viewAllNotification" onClick={handleNavigateToNotifications}>
        View All Notifications
      </StyledFooter>
    </StyledWrapper>
  );
};

export default HeaderFlyout;
